import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { sendChatRequest } from '../services/chatService';

interface ChatResponse {
  response: string;
  ads: Array<{
    id: number;
    title: string;
    description: string;
    category: string;
    target_keywords: string[];
    ctr: number;
  }>;
  keywords: string[];
}

const Home = () => {
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState<ChatResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const result = await sendChatRequest(prompt);
      setResponse(result);
    } catch (error) {
      console.error('Error:', error);
      setError('Error occurred while processing your request.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Reasonic Demo
      </Typography>
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Enter your prompt here..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            disabled={isLoading || !prompt.trim()}
          >
            Submit
          </Button>
        </form>
      </Paper>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {response && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Response
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{response.response}</Typography>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Extracted Keywords
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {response.keywords.map((keyword, index) => (
                <Chip key={index} label={keyword} color="primary" variant="outlined" />
              ))}
            </Stack>
          </Paper>

          {response.ads.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Matched Advertisements
              </Typography>
              <Stack spacing={2}>
                {response.ads.map((ad) => (
                  <Card key={ad.id} variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {ad.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {ad.description}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mt={1}>
                        {ad.target_keywords.map((keyword, index) => (
                          <Chip
                            key={index}
                            label={keyword}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default Home; 